import React, { useCallback, useEffect, useState } from 'react'
import { Box, Typography } from '@mui/material'
import TimerIcon from '@mui/icons-material/Timer'

import type { IBlockPayloadCountdown, ICountdownColors } from '../../../types'

const DEFAULT_COLORS: Required<ICountdownColors> = {
  bg: '#1e1b4b',
  bgGradient: true,
  bgFrom: '#1e1b4b',
  bgTo: '#312e81',
  bgAngle: 135,
  digitBg: '#6366f1',
  digitColor: '#ffffff',
  textColor: 'rgba(255,255,255,0.85)',
  iconColor: '#F97316',
}

function useCountdown(expireAt: string) {
  const calc = useCallback(() => {
    const diff = new Date(expireAt).getTime() - Date.now()
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true }
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff % 86400000) / 3600000),
      minutes: Math.floor((diff % 3600000) / 60000),
      seconds: Math.floor((diff % 60000) / 1000),
      expired: false,
    }
  }, [expireAt])

  const [remaining, setRemaining] = useState(calc)
  useEffect(() => {
    setRemaining(calc())
    const id = setInterval(() => setRemaining(calc()), 1000)
    return () => clearInterval(id)
  }, [calc])

  return remaining
}

export const CountdownRenderer: React.FC<{ payload: IBlockPayloadCountdown }> = ({ payload }) => {
  const colors: Required<ICountdownColors> = { ...DEFAULT_COLORS, ...payload.colors }
  const { days, hours, minutes, seconds, expired } = useCountdown(payload.expireAt)
  const pad2 = (n: number) => String(n).padStart(2, '0')

  const bg = colors.bgGradient && colors.bgFrom && colors.bgTo
    ? `linear-gradient(${colors.bgAngle ?? 135}deg, ${colors.bgFrom} 0%, ${colors.bgTo} 100%)`
    : (colors.bg || DEFAULT_COLORS.bg)

  const digitBox = (value: string, label: string) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
      <Box
        sx={{
          fontFamily: '"JetBrains Mono", "SF Mono", "Fira Code", monospace',
          fontWeight: 800,
          fontSize: { xs: 32, sm: 40 },
          lineHeight: 1,
          minWidth: { xs: 56, sm: 68 },
          py: { xs: 1.5, sm: 2 },
          px: 1,
          borderRadius: 2,
          textAlign: 'center',
          color: colors.digitColor,
          bgcolor: colors.digitBg,
          boxShadow: `0 4px 20px ${colors.digitBg}44`,
          position: 'relative',
          overflow: 'hidden',
          '&::after': {
            content: '""',
            position: 'absolute',
            left: '8%',
            right: '8%',
            top: '50%',
            height: '1px',
            bgcolor: 'rgba(255,255,255,0.15)',
          },
        }}
      >
        {value}
      </Box>
      <Typography
        variant="caption"
        sx={{ fontWeight: 600, color: colors.textColor, letterSpacing: 1, fontSize: 11 }}
      >
        {label}
      </Typography>
    </Box>
  )

  const colon = (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: { xs: 1.5, sm: 2 } }}>
        <Typography
          sx={{
            fontFamily: 'monospace',
            fontWeight: 800,
            fontSize: { xs: 28, sm: 36 },
            color: colors.textColor,
            opacity: 0.4,
            lineHeight: 1,
            userSelect: 'none',
          }}
        >
          :
        </Typography>
      </Box>
      <Typography variant="caption" sx={{ fontSize: 11, visibility: 'hidden' }}>&nbsp;</Typography>
    </Box>
  )

  return (
    <Box
      sx={{
        p: { xs: 3, sm: 4 },
        borderRadius: 3,
        textAlign: 'center',
        background: bg,
        overflow: 'hidden',
      }}
    >
      {payload.title && (
        <Typography
          variant="subtitle1"
          sx={{ color: colors.textColor, fontWeight: 600, mb: 2.5, letterSpacing: 0.5 }}
        >
          <TimerIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'text-bottom', color: colors.iconColor }} />
          {payload.title}
        </Typography>
      )}
      {expired ? (
        <Typography variant="h5" sx={{ color: colors.iconColor, fontWeight: 700, py: 3 }}>
          {payload.expiredText || 'หมดเวลาแล้ว!'}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: { xs: 1, sm: 1.5 }, flexWrap: 'wrap' }}>
          {digitBox(String(days), 'วัน')}
          {colon}
          {digitBox(pad2(hours), 'ชั่วโมง')}
          {colon}
          {digitBox(pad2(minutes), 'นาที')}
          {colon}
          {digitBox(pad2(seconds), 'วินาที')}
        </Box>
      )}
    </Box>
  )
}
